import { sendCustomRUMEvent } from "./awsRUM";
import {
  ALLEGRO_PURCHASE_LINK_EVENT,
  HEADPHONE_TYPE,
} from "../constants/constants";
import { HEADPHONE_URL_TO_TYPE_MAP } from "../constants/paths";
import { ELIGIBILITY_STATUS } from "../constants/constants";
import { COUNTRY_CODES_THREE_LETTER } from "../constants/constants";
import { LOCALE_VALUES } from "src/constants";
import {
  PLUGFONES_MERCENARY_LT_EN_US,
  PLUGFONES_MERCENARY_LT_EN_CA,
  PLUGFONES_MERCENARY_LT_FR_CA,
  PLUGFONES_ALLERT_EN_US,
  PLUGFONES_ALLERT_EN_CA,
  PLUGFONES_ALLERT_FR_VA,
  PLUGFONES_FREE_REIGN_VT_EN_US,
  PLUGFONES_FreeReign_VL_FR_CA,
} from "../constants/paths";

export const extractFromUserInfo = (userInfo) => {
  const payload = userInfo.signInUserSession?.idToken?.payload;
  if (!payload)
    return {
      alias: "",
      userName: "",
      siteCode: "",
      businessUnit: "",
      countryCode: "",
    };

  const alias = extractAliasFromUser(payload);
  const userName = extractFullNameFromUser(payload);
  const siteCode = extractLocationFromUser(payload);
  const businessUnit = extractBusinessUnitFromUser(payload);
  const countryCode = extractCountryCodeFromUser(payload);
  return { alias, userName, siteCode, businessUnit, countryCode };
};

export const isCountryCodeCanada = (countryCode) => {
  return countryCode === COUNTRY_CODES_THREE_LETTER.CAN;
};

const STAGE = "beta";
/**
 * Returns the current stage as a trimmed, lowercase string
 */
export function getStage() {
  return STAGE.trim().toLowerCase();
}

export const isLocal = () => getStage() === "dev";
export const isPersonal = () => getStage() === "yuanxuxu";
export const isBeta = () => getStage() === "beta";
export const isGamma = () => getStage() === "gamma";
export const isProd = () => getStage() === "prod";

export const isDevEnv = () => isLocal() || isPersonal() || isBeta();

export const getFooterIconDimension = (isMobile, isTablet) => {
  let width = 88,
    height = 66;
  if (isMobile) {
    width = 44;
    height = 33;
  } else if (isTablet) {
    width = 70;
    height = 53;
  }
  return { height, width };
};

export const generateKey = (pre) => {
  return `${pre}_${new Date().getTime()}`;
};

export const onClickUrl = (url) => {
  return () => {
    openInNewTab(url);
  };
};

export const onClickPurchaseUrl = (url) => {
  return () => {
    sendCustomRUMEvent(ALLEGRO_PURCHASE_LINK_EVENT, {
      [HEADPHONE_TYPE]: HEADPHONE_URL_TO_TYPE_MAP[url],
    });
    openInNewTab(url);
  };
};

export const openInNewTab = (url) => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

export const separatePathsByEligibility = (processPathPairs) => {
  const approvedPaths = [];
  const notApprovedPaths = [];
  processPathPairs.forEach(([processPath, eligbility]) => {
    eligbility === ELIGIBILITY_STATUS.YES
      ? approvedPaths.push(processPath)
      : notApprovedPaths.push(processPath);
  });
  return [approvedPaths, notApprovedPaths];
};

export const scrollToElement = (targetElementId, behavior) => {
  var targetElement = document.getElementById(targetElementId);

  if (targetElement) {
    targetElement.scrollIntoView({ behavior: behavior ?? "smooth" });
  }
};

export const isEnglishLocale = (language) => {
  return [LOCALE_VALUES.EN_CA, LOCALE_VALUES.EN_US].includes(language);
};

const VIDEO_SOURCES = {
  [LOCALE_VALUES.FR_CA]:
    "https://www.youtube.com/embed/-_uUp5DjFgo?si=zGLuoajn8-eQYuTe",
  [LOCALE_VALUES.ES_MX]:
    "https://www.youtube.com/embed/cr7HHXFMBzc?si=iev-oVZ9RD0CJvam",
  ["ENGLISH"]: "https://www.youtube.com/embed/sSo40jf9R6U?si=P7Cj-2RoCJ36Yct3",
};

export const getVideoSourceByLanguage = (language) => {
  const lang = isEnglishLocale(language) ? "ENGLISH" : language;
  return `${VIDEO_SOURCES[lang]}&rel=0`;
};

const extractFullNameFromUser = (payload) => {
  const givenName = payload["given_name"] ?? "";
  const familyName = payload["family_name"] ?? "";
  return `${givenName} ${familyName}`;
};

const extractLocationFromUser = (payload) => {
  return payload["custom:AMZN_LOC_DESCR"]?.split("-")[0].trim() ?? "";
};

const extractAliasFromUser = (payload) => {
  return payload.identities[0]?.userId ?? "";
};

const extractBusinessUnitFromUser = (payload) => {
  return payload["custom:AMZN_BUSINESS_UNIT"] ?? "";
};

const extractCountryCodeFromUser = (payload) => {
  return payload["custom:CO"] ?? "";
};

const PRODUCT_URLS = {
  MERCENARY_LT: {
    [LOCALE_VALUES.EN_US]: PLUGFONES_MERCENARY_LT_EN_US,
    [LOCALE_VALUES.EN_CA]: PLUGFONES_MERCENARY_LT_EN_CA,
    [LOCALE_VALUES.FR_CA]: PLUGFONES_MERCENARY_LT_FR_CA,
  },
  FREE_REIGN_VT: {
    [LOCALE_VALUES.EN_US]: PLUGFONES_FREE_REIGN_VT_EN_US,
    [LOCALE_VALUES.EN_CA]: PLUGFONES_FREE_REIGN_VT_EN_US, // no CA specific
    [LOCALE_VALUES.FR_CA]: PLUGFONES_FreeReign_VL_FR_CA,
  },
  ALLERT: {
    [LOCALE_VALUES.EN_US]: PLUGFONES_ALLERT_EN_US,
    [LOCALE_VALUES.EN_CA]: PLUGFONES_ALLERT_EN_CA,
    [LOCALE_VALUES.FR_CA]: PLUGFONES_ALLERT_FR_VA,
  },
};

const getProductUrlByLanguage = (productType, language) => {
  if (!PRODUCT_URLS[productType]) {
    throw new Error(`Invalid product type: ${productType}`);
  }
  return PRODUCT_URLS[productType][language];
};

export const getMercenaryLTUrlByLanguage = (language) => {
  return getProductUrlByLanguage("MERCENARY_LT", language);
};

export const getPlugfonesFreeReignVTUrls = (language) => {
  return getProductUrlByLanguage("FREE_REIGN_VT", language);
};

export const getPlugfonesAllertUrls = (language) => {
  return getProductUrlByLanguage("ALLERT", language);
};

export const doesSiteBelongToSortCenter = (siteCode) => {
  return ["OWD9", "OWD5", "MGE5", "BOI5"].includes(siteCode);
};